/**
 * The registrar's account at a connection's provider (registry or upstream registrar), shown on
 * the address tab for whatever the provider's account API offers (`account_features`): the
 * prepaid balance / deposit, and the list of names the provider holds for the registrar compared
 * with the platform. One interface on the server (`RegistrarAccountApi`), implemented per
 * provider; the server asks a provider for the balance at most once a minute and keeps its name
 * list 10 minutes, so the list is paged, filtered and searched on the server.
 *
 * A name only the provider lists ("Chưa có trong hệ thống") can be read at the registry ("Xem tại
 * registry", read only) and brought into the platform ("Đưa vào hệ thống", one name or a selected
 * set, one after another with each name's result): no registry command, no charge. "Đưa tất cả vào
 * hệ thống" brings every such name in for one customer as a background run of the server; its
 * progress is shown above the list and read again every 5 s while it runs.
 */

import { ArrowDownToLine, CheckCircle2, CircleDashed, Eye, Globe2, Loader2, RefreshCw, Wallet, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { api, isAbortError, isApiError } from '../../../api';
import type { CustomerSummary } from '../../../api/areas/accounts';
import type {
  AccountBalance,
  AccountDomainRow,
  AccountDomains,
  AccountDomainsFilter,
  BalanceErrorDetails,
  ConnectionDetail,
  EnvironmentDetail,
  ImportAllRun,
  RegistryDomainPreview,
} from '../../../api/areas/integrations';
import { Badge } from '../../../components/Badge';
import { useSelection } from '../../../components/BulkList';
import { Callout, DataTable, Field, Pager, type Column } from '../../../components/blocks';
import { KeyValue } from '../../../components/KeyValue';
import { Loading } from '../../../components/States';
import { WriteError } from '../../../components/Write';
import { errorText } from '../../../lib/errorText';
import { formatCount, formatDate, formatDateTime, formatMoney, formatRelative } from '../../../lib/format';
import { newIdempotencyKey } from '../../../lib/ids';
import { useApi } from '../../../lib/useApi';
import { LIFECYCLE, StatusBadge } from '../../domains/status';
import { CustomerPicker } from '../../operator/RegisterDomain';
import { selectClass } from '../../operator/shared';
import { EnvBadge, Panel, tx, useIntegrationContext, WithIntegrationContextQuiet } from '../shared';

type BalanceState = { phase: 'loading' } | { phase: 'done'; balance: AccountBalance; seq: number } | { phase: 'error'; error: unknown; seq: number };

function detailsOf(error: unknown): BalanceErrorDetails {
  return isApiError(error) && error.details && typeof error.details === 'object' ? (error.details as BalanceErrorDetails) : {};
}

/** Refresh button that stays disabled for `seconds` (the server's cache interval). Mount it
 * with a new `key` for every answer so the countdown starts again. */
function RefreshButton({ seconds, busy, onRefresh }: { seconds: number; busy: boolean; onRefresh: () => void }) {
  const { t } = useTranslation('integrations');
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (left <= 0) return undefined;
    const id = window.setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left]);
  const wait = busy ? 0 : left;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" size="sm" variant="outline" onClick={onRefresh} disabled={busy || wait > 0}>
        <RefreshCw className={busy ? 'animate-spin' : undefined} /> {t('balance.refresh')}
      </Button>
      {wait > 0 ? <span className="text-xs text-muted-foreground">{t('balance.refreshIn', { count: wait })}</span> : null}
    </div>
  );
}

/** How the provider is named to the operator, and whether its account API is configured on the
 * server (VNNIC's Registrar API) rather than on the connection. */
type Provider = { name: string; serverConfigured: boolean };

function providerOf(connection: ConnectionDetail): Provider {
  if (connection.adapter === 'webnic') return { name: 'WebNIC', serverConfigured: false };
  if (connection.adapter === 'vnnic-epp') return { name: 'VNNIC', serverConfigured: true };
  return { name: connection.name, serverConfigured: connection.protocol === 'EPP' };
}

type Blocked = 'notConfigured';

/** Why the account cannot be asked for, before calling the server. A REST provider (WebNIC)
 * uses the connection's address and credential; an EPP registry (VNNIC) uses its registrar API
 * configured on this server. */
function unavailableReason(connection: ConnectionDetail, detail: EnvironmentDetail): Blocked | null {
  if (connection.protocol === 'EPP') return null;
  const credential = detail.credentials.find((c) => c.status === 'ACTIVE');
  if (!detail.exists || !detail.active?.rest?.base_url || !credential || credential.secret.destroyed) return 'notConfigured';
  return null;
}

function BlockedNote({ reason }: { reason: Blocked }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  return (
    <p className="text-sm text-muted-foreground">
      {t(`balance.unavailable.${reason}`, { deployment: t(`deployment.${ctx.deployment_environment}`) })}
    </p>
  );
}

export function BalanceCard({ connection, detail }: { connection: ConnectionDetail; detail: EnvironmentDetail }) {
  const { t } = useTranslation('integrations');
  const blocked = unavailableReason(connection, detail);
  const activeCredential = detail.credentials.find((c) => c.status === 'ACTIVE')?.version ?? null;
  return (
    <Panel
      title={
        <span className="inline-flex items-center gap-2">
          <Wallet className="size-4 text-muted-foreground" aria-hidden="true" /> {t('balance.title')}
        </span>
      }
      description={t('balance.description')}
    >
      {blocked ? (
        <BlockedNote reason={blocked} />
      ) : (
        // A new address or credential version is asked for straight away.
        <BalanceView key={`${connection.id}:${detail.active?.version ?? ''}:${activeCredential ?? ''}`} connectionId={connection.id} provider={providerOf(connection)} />
      )}
    </Panel>
  );
}

function BalanceView({ connectionId, provider }: { connectionId: string; provider: Provider }) {
  const { t } = useTranslation('integrations');
  const [state, setState] = useState<BalanceState>({ phase: 'loading' });
  const controller = useRef<AbortController | null>(null);
  const seq = useRef(0);

  const fetchBalance = useCallback(() => {
    controller.current?.abort();
    const c = new AbortController();
    controller.current = c;
    api
      .getAccountBalance(connectionId, { signal: c.signal })
      .then((balance) => {
        if (!c.signal.aborted) setState({ phase: 'done', balance, seq: ++seq.current });
      })
      .catch((error: unknown) => {
        if (c.signal.aborted || isAbortError(error)) return;
        setState({ phase: 'error', error, seq: ++seq.current });
      });
  }, [connectionId]);

  useEffect(() => {
    fetchBalance();
    return () => controller.current?.abort();
  }, [fetchBalance]);

  const load = () => {
    setState({ phase: 'loading' });
    fetchBalance();
  };

  const seconds = state.phase === 'done' ? state.balance.next_refresh_after_seconds : state.phase === 'error' ? (detailsOf(state.error).retry_after_seconds ?? 0) : 0;
  const refresh = <RefreshButton key={state.phase === 'loading' ? 'loading' : state.seq} seconds={seconds} busy={state.phase === 'loading'} onRefresh={load} />;

  if (state.phase === 'loading') return <Loading rows={1} label={t('balance.loading')} />;
  if (state.phase === 'error') {
    return (
      <div className="space-y-3">
        <BalanceError error={state.error} provider={provider} />
        {refresh}
      </div>
    );
  }
  const b = state.balance;
  const [first, ...others] = b.balances;
  return (
    <div className="space-y-3">
      {first ? (
        <div className="space-y-1">
          <div className="text-2xl font-semibold tabular">{formatMoney({ amount: first.amount, currency: first.currency })}</div>
          {others.length ? (
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {others.map((x) => (
                <li key={x.currency} className="tabular">
                  {formatMoney({ amount: x.amount, currency: x.currency })}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('balance.empty')}</p>
      )}
      <p className="text-xs text-muted-foreground">
        <span title={formatDateTime(b.fetched_at)}>{t('balance.fetchedAt', { when: formatDateTime(b.fetched_at), ago: formatRelative(b.fetched_at) })}</span>
        {b.cached ? ` ${t('balance.cached')}` : ''}
      </p>
      {refresh}
    </div>
  );
}

function BalanceError({ error, provider }: { error: unknown; provider: Provider }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  if (!isApiError(error)) return <Callout tone="crit" title={t('balance.error.failed')} />;
  const d = detailsOf(error);
  const code = error.code;
  const vars = { deployment: t(`deployment.${ctx.deployment_environment}`), provider: provider.name };
  const title = tx(`balance.error.${code}`, t('balance.error.failed', vars), vars);
  return (
    <Callout tone={code === 'UNAVAILABLE' ? 'warn' : 'crit'} title={title}>
      {code === 'AUTH_FAILED' ? (
        <div className="space-y-1">
          <p>{provider.serverConfigured ? t('balance.error.authFailedVnnic') : t('balance.error.authFailedBody', vars)}</p>
          {d.hint === 'WEBNIC_IP_ALLOW_LIST' ? <p>{t('webnic.allowListHow')}</p> : null}
        </div>
      ) : code === 'UNAVAILABLE' ? (
        <p>{t('balance.error.unavailableBody', vars)}</p>
      ) : code === 'NOT_CONFIGURED' && provider.serverConfigured ? (
        <p>
          {t('balance.error.notConfiguredServer', vars)}{' '}
          <Link to="/settings/vnnic?tab=registrar" className="font-medium text-primary hover:underline">
            {t('balance.error.openProviderSettings')}
          </Link>
        </p>
      ) : code === 'NOT_CONFIGURED' ? (
        <p>{t('balance.error.notConfiguredBody', vars)}</p>
      ) : null}
      {d.fetched_at ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {t('balance.error.when', { ...vars, when: formatDateTime(d.fetched_at) })}
          {d.cached ? ` ${t('balance.cached')}` : ''}
        </p>
      ) : null}
      <p className="mt-1 text-xs text-muted-foreground">{t('errors.meta', { code, requestId: error.requestId })}</p>
    </Callout>
  );
}

const PAGE_SIZE = 50;
const FILTERS: readonly AccountDomainsFilter[] = ['all', 'only_provider', 'in_both', 'only_platform'];

/** A name only the provider lists: it can be brought into the platform. */
const isCandidate = (d: AccountDomainRow) => d.at_provider && !d.platform;

/** The names the provider holds for the registrar, compared with the platform's names. */
export function DomainsCard({ connection, detail }: { connection: ConnectionDetail; detail: EnvironmentDetail }) {
  const { t } = useTranslation('integrations');
  const blocked = unavailableReason(connection, detail);
  return (
    <Panel
      title={
        <span className="inline-flex items-center gap-2">
          <Globe2 className="size-4 text-muted-foreground" aria-hidden="true" /> {t('accountDomains.title')}
        </span>
      }
      description={t('accountDomains.description')}
    >
      {blocked ? <BlockedNote reason={blocked} /> : <DomainsView connectionId={connection.id} provider={providerOf(connection)} />}
    </Panel>
  );
}

function DomainsView({ connectionId, provider }: { connectionId: string; provider: Provider }) {
  const { t } = useTranslation('integrations');
  const [filter, setFilter] = useState<AccountDomainsFilter>('all');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  // "Làm mới": the next request asks the provider again.
  const refresh = useRef(false);
  const [nonce, setNonce] = useState(0);
  const [viewing, setViewing] = useState<string | null>(null);
  const [importing, setImporting] = useState<string[] | null>(null);
  const selection = useSelection<AccountDomainRow>((d) => d.name);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setQ(typed.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(id);
  }, [typed]);
  const result = useApi(
    (signal) => {
      const again = refresh.current;
      refresh.current = false;
      return api.getAccountDomains(connectionId, { page, limit: PAGE_SIZE, filter, q: q || undefined, refresh: again || undefined }, { signal });
    },
    [connectionId, page, filter, q, nonce],
  );
  // The page shown stays while the next one loads.
  const [last, setLast] = useState<AccountDomains | null>(null);
  useEffect(() => {
    if (result.status === 'success') setLast(result.data);
  }, [result.status, result.data]);
  const data = result.status === 'success' ? result.data : last;
  const importAll = useImportAll(connectionId);
  const [importAllOpen, setImportAllOpen] = useState(false);
  const running = isRunning(importAll.run);
  // A finished run changes the comparison: read the page again.
  const wasRunning = useRef(false);
  const { reload } = result;
  useEffect(() => {
    if (wasRunning.current && !running) reload();
    wasRunning.current = running;
  }, [running, reload]);
  if (result.status === 'error' && !data) {
    return (
      <div className="space-y-3">
        <BalanceError error={result.error} provider={provider} />
        <Button size="sm" variant="outline" onClick={result.reload}>
          <RefreshCw /> {t('balance.refresh')}
        </Button>
      </div>
    );
  }
  if (!data) return <Loading rows={3} label={t('accountDomains.loading')} />;
  const loading = result.status === 'loading';
  const rows = data.items;
  const pages = Math.max(1, Math.ceil(data.total / data.limit));
  const from = data.total ? (data.page - 1) * data.limit + 1 : 0;
  const select = selection.column(rows.filter(isCandidate), (d) => t('accountDomains.select', { name: d.name }), t('accountDomains.selectPage'));
  const columns: Column<AccountDomainRow>[] = [
    { ...select, cell: (d) => (isCandidate(d) ? select.cell(d) : null) },
    {
      key: 'name',
      header: t('accountDomains.col.name'),
      cell: (d) =>
        d.platform ? (
          <Link to={`/domains/${encodeURIComponent(d.platform.name)}`} className="font-medium text-primary hover:underline">
            {d.name}
          </Link>
        ) : (
          <span className="font-medium">{d.name}</span>
        ),
    },
    { key: 'type', header: t('accountDomains.col.type'), cell: (d) => d.type ?? '—' },
    { key: 'created', header: t('accountDomains.col.created'), cell: (d) => d.created ?? '—' },
    { key: 'expires', header: t('accountDomains.col.expires'), cell: (d) => d.expires ?? '—' },
    {
      key: 'platform',
      header: t('accountDomains.col.platform'),
      cell: (d) =>
        d.platform ? (
          <span className="inline-flex flex-wrap items-center gap-1">
            <StatusBadge map={LIFECYCLE} value={d.platform.lifecycle_state} />
            {d.at_provider ? null : <Badge tone="warn">{t('accountDomains.notAtProvider')}</Badge>}
          </span>
        ) : (
          <Badge tone="warn">{t('accountDomains.notInPlatform')}</Badge>
        ),
    },
    {
      key: 'actions',
      header: t('accountDomains.col.actions'),
      cell: (d) =>
        isCandidate(d) ? (
          <span className="inline-flex flex-wrap gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => setViewing(d.name)}>
              <Eye /> {t('accountDomains.viewRegistry')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setImporting([d.name])}>
              <ArrowDownToLine /> {t('accountDomains.importOne')}
            </Button>
          </span>
        ) : null,
    },
  ];
  const s = data.summary;
  const pick = (f: AccountDomainsFilter) => {
    setFilter(f);
    setPage(1);
  };
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        {(
          [
            ['atProvider', s.at_provider, false],
            ['inBoth', s.in_both, false],
            ['onlyProvider', s.only_provider, s.only_provider > 0],
            ['onlyPlatform', s.only_platform, s.only_platform > 0],
          ] as const
        ).map(([k, n, warn]) => (
          <div key={k} className="rounded-md border p-3">
            <dt className="text-xs text-muted-foreground">{t(`accountDomains.summary.${k}`)}</dt>
            <dd className={warn ? 'text-lg font-semibold text-warn' : 'text-lg font-semibold'}>{formatCount(n)}</dd>
          </div>
        ))}
      </dl>
      {importAll.run ? <ImportAllPanel run={importAll.run} /> : null}
      {s.only_provider > 0 ? (
        <Callout tone="info">
          <p>{t('accountDomains.onlyProviderHint', { count: s.only_provider })}</p>
          <Button type="button" size="sm" className="mt-2" onClick={() => setImportAllOpen(true)} disabled={running}>
            <ArrowDownToLine /> {t('accountDomains.importAll.button')}
          </Button>
        </Callout>
      ) : null}
      {result.status === 'error' ? <BalanceError error={result.error} provider={provider} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => pick(f)} aria-pressed={filter === f}>
            {t(`accountDomains.filter.${f}`)}
          </Button>
        ))}
        <input
          type="search"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={t('accountDomains.search')}
          aria-label={t('accountDomains.search')}
          className="ml-auto h-8 w-full rounded-md border bg-background px-2 text-sm sm:w-56"
        />
      </div>
      {selection.count ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-card px-3 py-2 shadow-sm" role="region" aria-label={t('accountDomains.selected', { count: selection.count })}>
          <span className="mr-1 text-sm font-medium">{t('accountDomains.selected', { count: selection.count })}</span>
          <Button type="button" size="sm" onClick={() => setImporting(selection.rows.map((d) => d.name))}>
            <ArrowDownToLine /> {t('accountDomains.importSelected', { count: selection.count })}
          </Button>
          <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={selection.clear}>
            {t('accountDomains.clearSelection')}
          </Button>
        </div>
      ) : null}
      <div className={loading ? 'opacity-60 transition-opacity' : undefined} aria-busy={loading}>
        <DataTable columns={columns} rows={rows} rowKey={(d) => d.name} empty={t('accountDomains.empty')} />
        <Pager
          canPrevious={data.page > 1 && !loading}
          canNext={data.page < pages && !loading}
          onPrevious={() => setPage(data.page - 1)}
          onNext={() => setPage(data.page + 1)}
          info={t('accountDomains.page', { page: data.page, pages, from, to: from ? from + rows.length - 1 : 0, total: formatCount(data.total) })}
        />
      </div>
      <div className="space-y-2 text-xs text-muted-foreground">
        <p>
          <span title={formatDateTime(data.fetched_at)}>{t('accountDomains.fetchedAt', { when: `${formatDateTime(data.fetched_at)} (${formatRelative(data.fetched_at)})` })}</span>{' '}
          {t('accountDomains.keptNote')}
        </p>
        <RefreshButton
          key={`${data.fetched_at}:${nonce}`}
          seconds={data.next_refresh_after_seconds}
          busy={loading}
          onRefresh={() => {
            refresh.current = true;
            setNonce((n) => n + 1);
          }}
        />
      </div>
      {viewing ? (
        <RegistrySheet
          connectionId={connectionId}
          name={viewing}
          onClose={() => setViewing(null)}
          onImport={() => {
            setImporting([viewing]);
            setViewing(null);
          }}
        />
      ) : null}
      {importing ? (
        <ImportDialog
          connectionId={connectionId}
          names={importing}
          onClose={(changed) => {
            setImporting(null);
            if (changed) {
              selection.clear();
              result.reload();
            }
          }}
        />
      ) : null}
      {importAllOpen ? (
        <ImportAllDialog
          connectionId={connectionId}
          count={s.only_provider}
          fetchedAt={data.fetched_at}
          onClose={(run) => {
            setImportAllOpen(false);
            if (run) importAll.setRun(run);
          }}
        />
      ) : null}
    </div>
  );
}

const RUNNING_STATES: readonly ImportAllRun['state'][] = ['PENDING', 'RUNNING', 'WAITING', 'RETRYING'];
const isRunning = (run: ImportAllRun | null | undefined) => Boolean(run && RUNNING_STATES.includes(run.state));

/** The connection's latest bulk import, read again every 5 s while it runs (`undefined`: not known,
 * e.g. the caller may not read it). */
function useImportAll(connectionId: string) {
  const [run, setRun] = useState<ImportAllRun | null | undefined>(undefined);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    api.getImportAll(connectionId, { signal: c.signal }).then(
      (x) => {
        if (!c.signal.aborted) setRun(x.run);
      },
      () => undefined,
    );
    return () => c.abort();
  }, [connectionId, nonce]);
  const running = isRunning(run);
  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setNonce((n) => n + 1), 5000);
    return () => window.clearInterval(id);
  }, [running]);
  return { run, setRun };
}

/** Where "Đưa tất cả vào hệ thống" stands: counts, the reasons of the skipped / failed names and
 * the latest of them. */
function ImportAllPanel({ run }: { run: ImportAllRun }) {
  const { t } = useTranslation('integrations');
  const running = isRunning(run);
  const sum = (m: Record<string, number>) => Object.values(m).reduce((a, b) => a + b, 0);
  const skipped = sum(run.skipped);
  const failed = sum(run.failed);
  const reasons = [
    ...Object.entries(run.skipped).map(([code, n]) => ['SKIPPED', code, n] as const),
    ...Object.entries(run.failed).map(([code, n]) => ['FAILED', code, n] as const),
  ];
  const label = (code: string) => errorText({ code, message: code, status: 409 }).text;
  const tone = running ? 'info' : run.state === 'COMPLETED' ? (failed ? 'warn' : 'ok') : 'crit';
  const pct = run.total ? Math.round((run.done / run.total) * 100) : 100;
  const progress = t('accountDomains.importAll.progress', { done: formatCount(run.done), total: formatCount(run.total) });
  return (
    <section className="space-y-3 rounded-md border p-3" aria-live="polite">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{t('accountDomains.importAll.panelTitle')}</h3>
        <Badge tone={tone}>{t(`accountDomains.importAll.state.${run.state}`)}</Badge>
        {running ? <Loader2 className="size-4 animate-spin text-primary" aria-hidden="true" /> : null}
        <span className="ml-auto text-xs text-muted-foreground">
          {t('accountDomains.importAll.meta', { customer: run.customer.display_name ?? run.customer.id ?? '—', when: formatDateTime(run.started_at) })}
        </span>
      </div>
      <div>
        <div className="h-2 overflow-hidden rounded bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={run.total} aria-valuenow={run.done} aria-label={progress}>
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{progress}</p>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
        {(
          [
            ['imported', run.imported],
            ['skipped', skipped],
            ['failed', failed],
            ['remaining', run.remaining],
          ] as const
        ).map(([k, n]) => (
          <div key={k} className="rounded-md border px-2 py-1.5">
            <dt className="text-xs text-muted-foreground">{t(`accountDomains.importAll.${k}`)}</dt>
            <dd className="font-semibold tabular">{formatCount(n)}</dd>
          </div>
        ))}
      </dl>
      {reasons.length ? (
        <div className="text-xs">
          <div className="font-medium text-muted-foreground">{t('accountDomains.importAll.byReason')}</div>
          <ul className="mt-1 space-y-0.5">
            {reasons.map(([outcome, code, n]) => (
              <li key={`${outcome}:${code}`}>
                {t(`accountDomains.importAll.outcome.${outcome}`)} · {label(code)}: <span className="tabular">{formatCount(n)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {run.issues.length ? (
        <details className="text-xs">
          <summary className="cursor-pointer font-medium">{t('accountDomains.importAll.issues', { count: run.issues.length })}</summary>
          <ul className="mt-1 max-h-60 divide-y overflow-y-auto rounded-md border">
            {[...run.issues].reverse().map((x, i) => (
              <li key={`${x.name}:${i}`} className="flex flex-wrap gap-x-2 px-2 py-1">
                <span className="font-medium break-words">{x.name}</span>
                <span className="text-muted-foreground">
                  {t(`accountDomains.importAll.outcome.${x.outcome}`)} · {label(x.code)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {run.failure ? <Callout tone="crit">{t('accountDomains.importAll.failure', { message: run.failure.message ?? run.failure.code })}</Callout> : null}
      {!running ? (
        <p className="text-xs text-muted-foreground">
          {run.completed_at ? `${t('accountDomains.importAll.finishedAt', { when: formatDateTime(run.completed_at) })} ` : ''}
          {failed || run.remaining ? t('accountDomains.importAll.rerunHint') : null}
        </p>
      ) : null}
    </section>
  );
}

/** "Đưa tất cả vào hệ thống": the customer every name goes to and a reason, then the server's run
 * starts (one per connection at a time). */
function ImportAllDialog({ connectionId, count, fetchedAt, onClose }: { connectionId: string; count: number; fetchedAt: string; onClose: (run: ImportAllRun | null) => void }) {
  const { t } = useTranslation('integrations');
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = useRef(newIdempotencyKey());
  const valid = Boolean(customer) && reason.trim().length > 0;
  const submit = async () => {
    if (!customer || busy) return;
    setBusy(true);
    setError(null);
    try {
      const started = await api.startImportAll(connectionId, { customer_id: customer.id, reason: reason.trim() }, { idempotencyKey: key.current });
      onClose(started.run);
    } catch (e) {
      // Refused: nothing started, a retry is a new request. No answer: the retry keeps the key.
      if (isApiError(e) && e.status >= 400 && e.status < 500) key.current = newIdempotencyKey();
      setError(e);
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose(null)}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('accountDomains.importAll.title')}</DialogTitle>
          <DialogDescription>{t('accountDomains.importAll.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) void submit();
          }}
        >
          <Callout tone="info">{t('accountDomains.importAll.count', { count, when: formatDateTime(fetchedAt) })}</Callout>
          <p className="text-sm text-muted-foreground">{t('accountDomains.importAll.skipNote')}</p>
          <Field label={t('accountDomains.importAll.customer')} group>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </Field>
          <Field label={t('accountDomains.import.reason')} hint={t('accountDomains.import.reasonHint')}>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required />
          </Field>
          {error ? <WriteError error={error} /> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onClose(null)} disabled={busy}>
              {t('accountDomains.import.close')}
            </Button>
            <Button type="submit" disabled={!valid || busy}>
              {busy ? <Loader2 className="animate-spin" /> : <ArrowDownToLine />} {t('accountDomains.importAll.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** What the registry holds for a name, read now ("Xem tại registry"), and whether it can be brought in. */
function PreviewFacts({ p }: { p: RegistryDomainPreview }) {
  const { t } = useTranslation('integrations');
  const r = p.registry;
  const m = p.registrant_match;
  const refusal = p.refusal ? errorText({ code: p.refusal.code, message: p.refusal.message, status: 409 }) : null;
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{t('accountDomains.preview.readAt', { when: formatDateTime(p.read_at) })}</p>
      {r ? (
        <KeyValue
          rows={[
            [t('accountDomains.preview.roid'), r.roid ?? '—'],
            [
              t('accountDomains.preview.sponsor'),
              r.sponsored_by_us ? (
                t('accountDomains.preview.sponsoredByUs', { sponsor: r.sponsor ?? '' })
              ) : (
                <Badge key="s" tone="crit">
                  {t('accountDomains.preview.sponsoredByOther', { sponsor: r.sponsor ?? '?' })}
                </Badge>
              ),
            ],
            [t('accountDomains.preview.created'), r.created_at ? formatDate(r.created_at) : '—'],
            [t('accountDomains.preview.expires'), r.expires_at ? formatDate(r.expires_at) : '—'],
            [t('accountDomains.preview.statuses'), r.statuses.length ? r.statuses.join(', ') : '—'],
            [t('accountDomains.preview.nameservers'), r.nameservers.length ? r.nameservers.join(', ') : '—'],
            [t('accountDomains.preview.registrant'), r.registrant ?? '—'],
            r.contacts.length > 0 && [t('accountDomains.preview.contacts'), r.contacts.map((c) => `${c.type}: ${c.handle}`).join(', ')],
          ]}
        />
      ) : (
        <Callout tone="warn">{t('accountDomains.preview.notFound')}</Callout>
      )}
      {r ? (
        <div className="space-y-1 text-sm">
          <div className="text-xs font-medium text-muted-foreground">{t('accountDomains.preview.subject')}</div>
          <p>{t(`accountDomains.match.${m.status}`, { count: m.status === 'AMBIGUOUS' ? m.subjects : 0, outcome: m.status === 'UNAVAILABLE' ? m.outcome : '' })}</p>
          {p.subject ? (
            <p className="text-xs text-muted-foreground">
              {p.subject.managing_customer
                ? t('accountDomains.preview.subjectManagedBy', { customer: p.subject.managing_customer.display_name ?? p.subject.managing_customer.id })
                : p.subject.hostbill
                  ? t('accountDomains.preview.subjectHostbill')
                  : null}{' '}
              <Link to={`/subjects/${encodeURIComponent(p.subject.id)}`} target="_blank" className="font-medium text-primary hover:underline">
                {t('accountDomains.preview.openSubject')}
              </Link>
            </p>
          ) : null}
        </div>
      ) : null}
      {p.platform ? (
        <Callout tone="info">
          {t('accountDomains.preview.inPlatform')}{' '}
          <Link to={`/domains/${encodeURIComponent(p.name)}`} className="font-medium text-primary hover:underline">
            {t('accountDomains.preview.openDomain')}
          </Link>
        </Callout>
      ) : p.importable ? (
        <Callout tone="ok">{t('accountDomains.preview.importable')}</Callout>
      ) : refusal ? (
        <Callout tone="warn" title={refusal.text}>
          {refusal.detail ? <p className="text-xs text-muted-foreground">{refusal.detail}</p> : null}
        </Callout>
      ) : null}
    </div>
  );
}

function RegistrySheet({ connectionId, name, onClose, onImport }: { connectionId: string; name: string; onClose: () => void; onImport: () => void }) {
  const { t } = useTranslation('integrations');
  const preview = useApi((signal) => api.previewRegistryDomain(connectionId, name, { signal }), [connectionId, name]);
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('accountDomains.preview.title', { name })}</SheetTitle>
          <SheetDescription>{t('accountDomains.preview.description')}</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 px-4 pb-6">
          {preview.status === 'loading' ? <Loading rows={4} label={t('accountDomains.preview.loading')} /> : null}
          {preview.status === 'error' ? <WriteError error={preview.error} /> : null}
          {preview.status === 'success' ? <PreviewFacts p={preview.data} /> : null}
          <div className="flex flex-wrap gap-2">
            {preview.status === 'success' && preview.data.importable ? (
              <Button type="button" size="sm" onClick={onImport}>
                <ArrowDownToLine /> {t('accountDomains.importOne')}
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="outline" onClick={preview.reload} disabled={preview.status === 'loading'}>
              <RefreshCw /> {t('balance.refresh')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              {t('accountDomains.preview.close')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

type ImportState = { phase: 'waiting' } | { phase: 'running' } | { phase: 'ok' } | { phase: 'failed'; note: string };

/**
 * "Đưa vào hệ thống" for one name (with what the registry holds) or a selected set: the owning
 * customer, the registrant (matched by the registry's identity number, or chosen for one name),
 * a reason; then each name is sent on its own, one after another, with its result. A failed name
 * can be retried (same key unless the server refused it).
 */
function ImportDialog({ connectionId, names, onClose }: { connectionId: string; names: string[]; onClose: (changed: boolean) => void }) {
  const { t } = useTranslation('integrations');
  const single = names.length === 1 ? names[0] : null;
  const preview = useApi((signal) => (single ? api.previewRegistryDomain(connectionId, single, { signal }) : Promise.resolve(null)), [connectionId, single]);
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [choose, setChoose] = useState(false);
  const [subject, setSubject] = useState('');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<Record<string, ImportState>>({});
  const [running, setRunning] = useState(false);
  const keys = useRef<Record<string, string>>({});
  const subjects = useApi(
    (signal) => (customer && choose ? api.listSubjects({ customer_id: customer.id, limit: 100 }, { signal }) : Promise.resolve({ items: [], next_after: null })),
    [customer?.id, choose],
  );
  const subjectItems = subjects.status === 'success' ? subjects.data.items : [];
  const started = Object.keys(state).length > 0;
  const values = Object.values(state);
  const done = values.filter((x) => x.phase === 'ok' || x.phase === 'failed').length;
  const ok = values.filter((x) => x.phase === 'ok').length;
  const failed = names.filter((n) => state[n]?.phase === 'failed');
  const p = preview.status === 'success' ? preview.data : null;
  const valid = Boolean(customer) && reason.trim().length > 0 && (!choose || Boolean(subject)) && (!single || p?.importable === true);

  const run = async (targets: readonly string[]) => {
    if (!customer) return;
    setRunning(true);
    setState((x) => ({ ...x, ...Object.fromEntries(targets.map((n) => [n, { phase: 'waiting' } as ImportState])) }));
    for (const n of targets) {
      setState((x) => ({ ...x, [n]: { phase: 'running' } }));
      const key = (keys.current[n] ??= newIdempotencyKey());
      let next: ImportState;
      try {
        await api.importRegistryDomain(connectionId, n, { customer_id: customer.id, subject_id: choose ? subject : null, reason: reason.trim() }, { idempotencyKey: key });
        next = { phase: 'ok' };
      } catch (e) {
        // Refused: nothing was created, a retry is a new request. No answer: the retry keeps the key.
        if (isApiError(e) && e.status >= 400 && e.status < 500) delete keys.current[n];
        next = { phase: 'failed', note: errorText(e).text };
      }
      setState((x) => ({ ...x, [n]: next }));
    }
    setRunning(false);
  };
  const close = () => {
    if (!running) onClose(values.some((x) => x.phase === 'ok'));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('accountDomains.import.title', { count: names.length })}</DialogTitle>
          <DialogDescription>{t('accountDomains.import.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !running) void run(names);
          }}
        >
          {single ? (
            preview.status === 'loading' ? (
              <Loading rows={3} label={t('accountDomains.preview.loading')} />
            ) : preview.status === 'error' ? (
              <WriteError error={preview.error} />
            ) : p ? (
              <PreviewFacts p={p} />
            ) : null
          ) : null}
          <Callout tone="info">{t('accountDomains.import.states')}</Callout>
          {!started ? (
            <>
              <Field label={t('accountDomains.import.customer')} group>
                <CustomerPicker
                  value={customer}
                  onChange={(c) => {
                    setCustomer(c);
                    setSubject('');
                  }}
                />
              </Field>
              <Field label={t('accountDomains.import.registrant')} group>
                {single ? (
                  <div className="grid gap-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="registrant" checked={!choose} onChange={() => setChoose(false)} />
                      {t('accountDomains.import.registrantAuto')}
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="registrant" checked={choose} onChange={() => setChoose(true)} />
                      {t('accountDomains.import.registrantChoose')}
                    </label>
                    {choose && customer ? (
                      subjects.status === 'success' && subjectItems.length === 0 ? (
                        <Callout tone="warn">{t('accountDomains.import.noSubjects')}</Callout>
                      ) : (
                        <select className={selectClass} value={subject} onChange={(e) => setSubject(e.target.value)} required aria-label={t('accountDomains.import.chooseSubject')}>
                          <option value="">{t('accountDomains.import.chooseSubject')}</option>
                          {subjectItems.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.legal_name ?? x.id}
                            </option>
                          ))}
                        </select>
                      )
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('accountDomains.import.bulkAutoOnly')}</p>
                )}
              </Field>
              <Field label={t('accountDomains.import.reason')} hint={t('accountDomains.import.reasonHint')}>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required />
              </Field>
            </>
          ) : (
            <p className="text-sm" aria-live="polite">
              {running ? t('accountDomains.import.running', { done, total: names.length }) : t('accountDomains.import.finished', { ok, failed: failed.length })}
            </p>
          )}
          {names.length > 1 || started ? (
            <ul className="max-h-72 divide-y overflow-y-auto rounded-md border text-sm">
              {names.map((n) => {
                const x = state[n];
                return (
                  <li key={n} className="flex items-start gap-2 px-3 py-2">
                    <ImportIcon state={x} />
                    <span className="min-w-0 font-medium break-words">{n}</span>
                    <span className="ml-auto min-w-0 text-right text-xs text-muted-foreground break-words">
                      {!x ? null : x.phase === 'ok' ? (
                        <Link to={`/domains/${encodeURIComponent(n)}`} className="font-medium text-primary hover:underline">
                          {t('accountDomains.import.openDomain')}
                        </Link>
                      ) : x.phase === 'failed' ? (
                        x.note
                      ) : x.phase === 'running' ? (
                        t('accountDomains.import.working')
                      ) : (
                        t('accountDomains.import.waiting')
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={running}>
              {t('accountDomains.import.close')}
            </Button>
            {!started ? (
              <Button type="submit" disabled={!valid}>
                <ArrowDownToLine /> {t('accountDomains.import.submit')}
              </Button>
            ) : failed.length && !running ? (
              <Button type="button" onClick={() => void run(failed)}>
                {t('accountDomains.import.retry', { count: failed.length })}
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportIcon({ state }: { state?: ImportState }) {
  const c = 'mt-0.5 size-4 shrink-0';
  if (!state || state.phase === 'waiting') return <CircleDashed className={`${c} text-muted-foreground`} aria-hidden="true" />;
  if (state.phase === 'running') return <Loader2 className={`${c} animate-spin text-primary`} aria-hidden="true" />;
  if (state.phase === 'ok') return <CheckCircle2 className={`${c} text-success`} aria-hidden="true" />;
  return <XCircle className={`${c} text-critical`} aria-hidden="true" />;
}

/** The provider account of a connection (balance, names held) on this server, as a block of another page (the registry page). Nothing when the provider offers no
 * account function or the caller may not read integrations. */
export function ProviderAccountBlock({ connectionId }: { connectionId: string }) {
  return (
    <WithIntegrationContextQuiet>
      <ProviderAccountInner connectionId={connectionId} />
    </WithIntegrationContextQuiet>
  );
}

function ProviderAccountInner({ connectionId }: { connectionId: string }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const env = ctx.integration_environment;
  const connection = useApi((signal) => api.getConnection(connectionId, { signal }), [connectionId]);
  const detail = useApi((signal) => api.getConnectionEnvironment(connectionId, { signal }), [connectionId]);
  if (connection.status !== 'success' || detail.status !== 'success') return null;
  const c = connection.data;
  if (!c.account_features?.balance && !c.account_features?.domain_list) return null;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">{t('accountBlock.title')}</h2>
        <EnvBadge env={env} long />
        <Link to={`/settings/registries/${encodeURIComponent(c.code)}/environments`} className="ml-auto text-sm text-primary hover:underline">
          {t('accountBlock.openConnection')}
        </Link>
      </div>
      {c.account_features?.balance ? <BalanceCard connection={c} detail={detail.data} /> : null}
      {c.account_features?.domain_list ? <DomainsCard connection={c} detail={detail.data} /> : null}
    </div>
  );
}
